// src/pages/BrokerList.jsx
import React, { useEffect, useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import AppConst from '../apis/appconst';
import NewBroker from '../models/NewBroker';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

const downloadCsv = (rows = [], filename = 'brokers.csv') => {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(','),
    ...rows.map(r => keys.map(k => {
      const val = r[k] === null || r[k] === undefined ? '' : String(r[k]).replace(/"/g, '""');
      return `"${val}"`;
    }).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const BrokerList = () => {
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ l1: [], search_key: '', page_no: 0, limit: 20 });
  const [isAdmin, setIsAdmin] = useState(false);
  const [editItem, setEditItem] = useState(null);

  const loadBrokers = async () => {
    try {
      const fd = new FormData();
      fd.append('search_key', formData.search_key || '');
      fd.append('page_no', formData.page_no + '');
      fd.append('limit', formData.limit + '');
      const res = await axios.post(AppConst.BASE_URL + AppConst.getBrokerListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        setFormData(prev => ({ ...prev, l1: Array.isArray(res.data.records) ? res.data.records : [], total: res.data.total || 0 }));
      } else {
        setFormData(prev => ({ ...prev, l1: [] }));
      }
    } catch (err) {
      console.error('loadBrokers error', err);
      toast.error('Failed to load brokers');
      setFormData(prev => ({ ...prev, l1: [] }));
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem('ldata');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setIsAdmin(parsed?.role_id === 1 || parsed?.role_id === '1');
      } catch (e) { /* ignore */ }
    }
    loadBrokers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    loadBrokers();
    // eslint-disable-next-line
  }, [formData.search_key, formData.page_no]);

  const openAddModal = () => {
    setEditItem(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    // row is broker_tbl row with `id`
    setEditItem(row);
    setShowModal(true);
  };

  const handleDelete = async (row) => {
    if (!isAdmin) { toast.error('Only admin can delete.'); return; }
    const result = await Swal.fire({
      title: `Delete ${row.fname || 'this broker'}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });
    if (!result.isConfirmed) return;
    try {
      const fd = new FormData();
      fd.append('id', row.id + '');
      const res = await axios.post(AppConst.BASE_URL + AppConst.delBrokerUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        toast.success('Deleted');
        await loadBrokers();
      } else {
        toast.error(res.data?.success || 'Delete failed');
      }
    } catch (err) {
      console.error('delete error', err);
      toast.error('Delete failed');
    }
  };

  const exportPdf = () => {
    const rows = filteredRows;
    const doc = new jsPDF('p', 'pt', 'a4');
    doc.setFontSize(14);
    doc.text('Broker List', 40, 40);
    const head = [['Sl. No.', 'Name', 'Email', 'Phone', 'DOB', 'Lab Comm', 'IPD Comm']];
    const body = rows.map((r, idx) => {
      const name = (r.fname || '') + (r.lname ? ' ' + r.lname : '');
      const email = r.email || '';
      const phone = r.mobile || '';
      const dob = r.dob || '';
      const lab = (r.lab_commission_type ? (r.lab_commission_type === 'fixed' ? 'Fixed ' : 'Percent ') : '') + (r.lab_commission || '');
      const ipd = (r.ipd_commission_type ? (r.ipd_commission_type === 'fixed' ? 'Fixed ' : 'Percent ') : '') + (r.ipd_commission || '');
      return [String(idx + 1), name, email, phone, dob, lab, ipd];
    });

    autoTable(doc, {
      startY: 60,
      head,
      body,
      styles: { fontSize: 10, cellPadding: 4 },
      margin: { left: 40, right: 40 },
      theme: 'grid',
    });
    doc.save(`brokers_${Date.now()}.pdf`);
  };

  const filteredRows = useMemo(() => {
    const s = (formData.search_key || '').trim().toLowerCase();
    if (!s) return formData.l1 || [];
    return (formData.l1 || []).filter(r =>
      (r.fname || '').toString().toLowerCase().includes(s) ||
      (r.lname || '').toString().toLowerCase().includes(s) ||
      (r.email || '').toString().toLowerCase().includes(s) ||
      (r.mobile || '').toString().toLowerCase().includes(s) ||
      (`SDC000101${r.id}` || '').toLowerCase().includes(s)
    );
  }, [formData.l1, formData.search_key]);

  const columns = useMemo(() => ([
    { name: 'Sl. No.', selector: (row, idx) => idx + 1, width: '80px' },
    { name: 'Name', selector: row => (row.fname || '') + (row.lname ? ' ' + row.lname : ''), sortable: true, grow: 1 },
    { name: 'Email', selector: row => row.email || '', sortable: true, width: '220px' },
    { name: 'Phone', selector: row => row.mobile || '', width: '140px' },
    { name: 'DOB', selector: row => row.dob || '', width: '120px' },
    { name: 'Lab Comm', selector: row => (row.lab_commission_type ? (row.lab_commission_type === 'fixed' ? 'Fixed ' : 'Percent ') : '') + (row.lab_commission || ''), width: '160px' },
    { name: 'IPD Comm', selector: row => (row.ipd_commission_type ? (row.ipd_commission_type === 'fixed' ? 'Fixed ' : 'Percent ') : '') + (row.ipd_commission || ''), width: '160px' },
    {
      name: 'Action',
      cell: row => (
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-sm btn-outline-primary" onClick={() => handleEdit(row)} disabled={!isAdmin} style={{ opacity: !isAdmin ? 0.5 : 1 }}>
            Edit
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row)} disabled={!isAdmin} style={{ opacity: !isAdmin ? 0.5 : 1 }}>
            Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '170px'
    }
  ]), [isAdmin]);

  const customStyles = {
    headCells: { style: { backgroundColor: '#f1f5fb', color: '#405189', fontSize: '14px', fontWeight: '700' } },
    rows: { style: { minHeight: '56px' } },
    cells: { style: { paddingLeft: '12px', paddingRight: '12px' } }
  };

  const onNewBrokerClose = (refresh = false) => {
    setShowModal(false);
    setEditItem(null);
    if (refresh) loadBrokers();
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="card" id="brokerList">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Brokers</h5>
            <div>
              <button className="btn btn-success me-2" onClick={openAddModal}>
                <i className="ri-add-line align-bottom me-1" /> Add Broker
              </button>
              <button className="btn btn-outline-secondary me-2" onClick={() => downloadCsv(filteredRows, 'brokers.csv')}>Export CSV</button>
              <button className="btn btn-outline-primary" onClick={exportPdf}>Export PDF</button>
            </div>
          </div>

          <div className="card-body border border-dashed border-end-0 border-start-0">
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <input className="form-control" placeholder="Search by name, email, contact..." type="text" value={formData.search_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, search_key: e.target.value }))} />
              </div>
            </div>
          </div>

          <div className="card-body pt-0">
            <div className="table-responsive table-card mb-1">
              <DataTable
                columns={columns}
                data={filteredRows}
                customStyles={customStyles}
                noHeader
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50]}
                highlightOnHover
                persistTableHead
              />
            </div>

            <NewBroker
              item={editItem}
              show={showModal}
              onClose={(refresh) => onNewBrokerClose(refresh)}
            />

          </div>
        </div>
      </div>
    </div>
  );
};

export default BrokerList;
